import type { ButtonHTMLAttributes, PropsWithChildren } from "react";
import { Link } from "react-router-dom";

type ButtonAppearanceModel = "primary" | "secondary" | "ghost";

interface ButtonPieceProps
  extends PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>> {
  appearance?: ButtonAppearanceModel;
  to?: string;
  href?: string;
  fullWidth?: boolean;
}

const appearanceClassNames: Record<ButtonAppearanceModel, string> = {
  primary:
    "bg-cmj-red text-white shadow-glow hover:bg-[#d80e19] focus-visible:outline-cmj-red",
  secondary:
    "bg-cmj-gold text-cmj-base hover:bg-[#ffda3d] focus-visible:outline-cmj-gold",
  ghost:
    "border border-current bg-transparent text-current hover:bg-white/10 focus-visible:outline-white",
};

const baseClassName =
  "inline-flex min-h-11 items-center justify-center rounded-md px-5 py-3 text-sm font-black uppercase transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

export const ButtonPiece = ({
  appearance = "primary",
  children,
  className = "",
  to,
  href,
  fullWidth = false,
  type = "button",
  ...buttonProps
}: ButtonPieceProps) => {
  const classNames = [
    baseClassName,
    `cinema-button cinema-button-${appearance}`,
    appearanceClassNames[appearance],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (to) {
    return (
      <Link className={classNames} to={to}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a className={classNames} href={href} rel="noreferrer" target="_blank">
        {children}
      </a>
    );
  }

  return (
    <button className={classNames} type={type} {...buttonProps}>
      {children}
    </button>
  );
};
